import * as React from 'react'
import { ComponentClass, FunctionComponent, ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Field } from '@fluentui/react-components'
import { FieldProps } from '@fluentui/react-field'
import { useTranslation } from 'react-i18next'

export type FormikSubFieldProps<CP, Values> = Omit<FormikFieldProps<CP, Values>, 'component'>

export type FormikFieldProps<CP, Values> = Omit<FieldProps, 'label'> & {
    fieldName: keyof Values
    component: FunctionComponent<CP> | ComponentClass<CP>
    componentProps?: Partial<Omit<CP, 'component' | 'value' | 'required'>>
    labelKey?: string
    hintKey?: string

    /**
     * Deprecated, but still here so we don't have to change all forms and test them at once.
     * @deprecated
     */
    context?: any
}

export function findValue<Values>(values: Values, fieldName: string): any {
    const fieldPath = fieldName.split('.')
    if (fieldPath.length > 1) {
        return findValue(values?.[fieldPath[0]] || {}, fieldPath.slice(1).join('.'))
    }
    return values?.[fieldPath[0]] || ''
}

export default function FormikField<CP, Values>(props: FormikFieldProps<CP, Values>): ReactElement {
    const { t: translate } = useTranslation()
    const { values, errors, handleChange, handleBlur } = useFormikContext<Values>()

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])
    const currentValue = useMemo(() => findValue(values, props.fieldName as string), [values])

    return (
        <Field
            label={props.labelKey ? translate(props.labelKey) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey) : undefined)}
            {...props}
            validationMessage={validationMessage}
            validationState={validationState}
        >
            <props.component
                required={props.required}
                onBlur={handleBlur(props.fieldName)}
                onChange={handleChange(props.fieldName)}
                value={currentValue}
                autoComplete="off"
                data-lpignore={'true'}
                {...(props.componentProps as any)}
            />
        </Field>
    )
}
